<template>
  <div class="content-wrapper">
    <div class="last-status-container">
      <div class="status-header">
        <h6>{{ $t('dashboard.laststatus') }}</h6>
      </div>
      <p>{{ $t('dashboard.updated') }} {{ formattedLastUpdated }}</p>
    </div>

    <!-- User Controls -->
    <div class="controls" style="position: sticky; top: 0; z-index: 1000;">
      <div class="left-controls">
        <button class="refresh-button" @click="refreshData" title="Refresh">
          <span class="material-icons">refresh</span>
        </button>
        <button class="refresh-button" @click="downloadData" title="Download" :disabled="downloading">
          <span class="material-icons" :class="{ 'loading': downloading }">download</span>
        </button>
      </div>
      <div class="right-controls">
        <div class="autocomplete-container">
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="$t('views.navbar.searchobjects')"
            class="autocomplete-input select-s2 browser-default small-contents"
            @focus="showSuggestions = true"
            @input="onInput"
            @keydown.up="moveSelection(-1)"
            @keydown.down="moveSelection(1)"
            @keydown.enter="selectHighlightedSuggestion"
            @keydown.esc="clearSearchClick"
          >
          <ul class="suggestions-list" v-if="showSuggestions && filteredSuggestions.length > 0">
            <li
              v-for="(suggestion, index) in filteredSuggestions"
              :key="suggestion"
              class="suggestion-item"
              :class="{ 'highlighted': index === selectedIndex }"
              @click="selectSuggestion(suggestion)"
            >
              {{ suggestion }}
            </li>
          </ul>
        </div>
        <button class="refresh-button" @click="clearSearchClick" title="Clear Search">
          <span class="material-icons">clear</span>
        </button>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="error-message">
      <span class="error-icon">⚠️</span> {{ errorMessage }}
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="spinner-container">
      <div class="spinner"></div>
    </div>

    <!-- No Data Message -->
    <div v-else-if="!errorMessage && (!processedStatusData || processedStatusData.length === 0)">
      {{ $t('dashboard.nodataavailable') }}
    </div>

    <!-- Table with Pagination -->
    <div v-else class="table-container">
      <div v-if="paginatedData.length > 0" class="table-wrapper">
        <table class="status-table">
          <thead>
            <tr>
              <th style="text-align: left;" @click="sort('Path')">
                {{ $t('views.alarmlist.path') }}
                <span v-if="sortKey === 'Path' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('OK')" v-b-tooltip.hover.right title="Ok">
                <span class="material-icons">check_circle</span>
                <span v-if="sortKey === 'OK' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('Error')" v-b-tooltip.hover.right :title="$t('error')">
                <span class="material-icons">error</span>
                <span v-if="sortKey === 'Error' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('Warning')" v-b-tooltip.hover.right :title="$t('warning')">
                <span class="material-icons">warning</span>
                <span v-if="sortKey === 'Warning' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('Maintenance')" v-b-tooltip.hover.right :title="$t('dashboard.inmaintenance')">
                <span class="material-icons">build</span>
                <span v-if="sortKey === 'Maintenance' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('None')" v-b-tooltip.hover.right :title="$t('dashboard.none')">
                <span class="material-icons">help</span>
                <span v-if="sortKey === 'None' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th @click="sort('OperationMode')">
                {{ $t('dashboard.operationmode') }}
                <span v-if="sortKey === 'OperationMode' && sortDirection !== 'default'">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th v-for="i in 10" :key="'dir-' + i">-{{ i }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in paginatedData" :key="item.RailObjectId" >
              <td class="clickable" style="text-align: left;" :title="item.Path" @click="goToDashboard3()">
                {{ item.Path }}
              </td>
              <td :class="{'bg-green': item.OK > 0}">{{ item.OK }}</td>
              <td :class="{'bg-red': item.Error > 0}">{{ item.Error }}</td>
              <td :class="{'bg-orange': item.Warning > 0}">{{ item.Warning }}</td>
              <td :class="{'bg-blue': item.Maintenance > 0}">{{ item.Maintenance }}</td>
              <td>{{ item.None }}</td>
              <td>{{ translateOperationMode(item.OperationMode) }}</td>
              <td v-for="(val, i) in item.Directions"
                  @click="goToMeasurement(item.RailObjectId, val.measurementId)"
                  :key="'val-' + i"
                  :class="[getSeverityColor(val.severity), { 'clickable': val.measurementId }]">
                {{ val.label }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div v-if="paginatedData.length > 0" class="pagination">
        <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
          <span class="material-icons">navigate_before</span>
        </button>
        <span>{{ $t('dashboard.page')}} {{ currentPage }} {{ $t('dashboard.of')}} {{ totalPages }}</span>
        <button @click="nextPage" :disabled="currentPage >= totalPages" class="pagination-btn">
          <span class="material-icons">navigate_next</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AuthenticationService from '../../services/AuthenticationService'
import DashboardService from '../../services/DashboardService'

export default {
  name: 'LastStatus',
  components: {},
  props: ['last-updated', 'companyId', 'companyName'],
  emits: ['updating'],
  data () {
    return {
      loading: false,
      downloading: false,
      currentUser: AuthenticationService.getUser(),
      statusData: [],
      processedStatusData: [],
      errorMessage: null,
      currentPage: 1,
      rowsPerPage: 10,
      sortKey: '',
      sortDirection: 'default',
      searchQuery: '',
      searchItems: {},
      selectedSearchItem: '',
      showSuggestions: false,
      selectedIndex: -1,
      isSelecting: false
    }
  },

  mounted () {
    window.addEventListener('resize', this.updateRowsPerPage)
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.updateRowsPerPage)
    document.removeEventListener('click', this.handleClickOutside)
  },

  watch: {
    companyId: {
      immediate: true,
      async handler (newCompanyId) {
        if (newCompanyId) {
          // Also clears search query when company id has changed
          await this.refreshData(newCompanyId)
        } else {
          this.processedStatusData = []
        }
      }
    },

    searchQuery (newVal) {
      if (!newVal) {
        this.showSuggestions = false
        this.selectedIndex = -1
      } else if (!this.isSelecting) {
        this.showSuggestions = true
        this.selectedIndex = -1
      }
      this.updateTable()
    },

    filteredSuggestions () {
      // Reset selectedIndex if upon filtered list changes
      if (this.selectedIndex >= this.filteredSuggestions.length) {
        this.selectedIndex = this.filteredSuggestions.length - 1
      } else if (this.selectedIndex < -1) {
        this.selectedIndex = -1
      }
    },

    '$i18n.locale': {
      immediate: true,
      handler (newLang, oldLang) {
        (async () => {
          if (oldLang !== undefined) {
            await this.fetchAllData(this.companyId)
          }
        })()
      }
    }
  },

  computed: {
    formattedLastUpdated () {
      if (!this.lastUpdated) return 'N/A'

      const date = new Date(this.lastUpdated)
      return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' +
        date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },

    filteredData () {
      let filteredData = [...this.processedStatusData]

      // Apply filtering based on selected search item
      if (this.selectedSearchItem && this.searchItems[this.selectedSearchItem]) {
        const filteredRailObjectIds = this.searchItems[this.selectedSearchItem]
        filteredData = filteredData.filter(item =>
          filteredRailObjectIds.includes(item.RailObjectId)
        )
      }

      return filteredData
    },

    paginatedData () {
      let sortedData = [...this.filteredData]

      // Apply sorting only if not in 'default' state
      if (this.sortDirection !== 'default' && this.sortKey) {
        sortedData.sort((a, b) => {
          const valueA = a[this.sortKey]
          const valueB = b[this.sortKey]

          // Handle string vs number sorting
          if (typeof valueA === 'string') {
            return this.sortDirection === 'asc'
              ? valueA.localeCompare(valueB)
              : valueB.localeCompare(valueA)
          }

          return this.sortDirection === 'asc'
            ? valueA - valueB
            : valueB - valueA
        })
      }

      const start = (this.currentPage - 1) * this.rowsPerPage
      const end = start + this.rowsPerPage
      return sortedData.slice(start, end)
    },

    totalPages () {
      return Math.max(1, Math.ceil(this.filteredData.length / this.rowsPerPage))
    },

    filteredSuggestions () {
      if (!this.searchQuery) return Object.keys(this.searchItems)
      const query = this.searchQuery.toLowerCase()
      return Object.keys(this.searchItems).filter(item =>
        item.toLowerCase().includes(query)
      )
    }
  },

  methods: {
    async fetchAllData (companyId) {
      this.loading = true
      this.errorMessage = null

      try {
        const [statusResponse, searchResponse] = await Promise.all([
          DashboardService.getLastStatus(this.currentUser.UserId, companyId),
          DashboardService.getLastStatusSearchItems(this.currentUser.UserId, companyId)
        ])
        this.statusData = statusResponse.data
        this.searchItems = searchResponse.data
        this.processStatusData()
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          this.errorMessage = 'Failed to load data: ' + error.response.data.error
        } else {
          this.errorMessage = error.message || 'Failed to load data. Please try again.'
        }
        this.statusData = []
        this.processedStatusData = []
        this.searchItems = {}
      } finally {
        this.loading = false
      }

      this.$nextTick(() => {
        this.updateRowsPerPage()
      })
    },

    processStatusData () {
      try {
        if (!this.statusData || !Array.isArray(this.statusData)) {
          this.processedStatusData = []
          return
        }

        this.processedStatusData = this.statusData.map(item => {
          const directions = new Array(10).fill({ label: '-', severity: null })

          if (Array.isArray(item.LR)) {
            item.LR.forEach((lr, index) => {
              if (index < 10 && lr) {
                directions[index] = {
                  label: lr.MeasurementDirection === 1 ? this.$t('charts.r') : this.$t('charts.n'),
                  severity: lr.Severity || null,
                  measurementId: lr.MeasurementID
                }
              }
            })
          }

          return {
            Path: item.Path || '-',
            OK: item.OK || 0,
            Error: item.Error || 0,
            Warning: item.Warning || 0,
            Maintenance: item.Maintenance || 0,
            None: item.None || 0,
            OperationMode: item.OperationMode || 'Unknown',
            Directions: directions,
            LR: item.LR || [],
            RailObjectId: item.RailObjectID
          }
        })
      } catch (error) {
        this.errorMessage = 'Error while processing data: ' + error
        this.processedStatusData = []
      }
    },

    sort (key) {
      if (this.sortKey === key) {
        if (this.sortDirection === 'asc') {
          this.sortDirection = 'desc'
        } else if (this.sortDirection === 'desc') {
          this.sortDirection = 'default'
        } else {
          this.sortDirection = 'asc'
        }
      } else {
        this.sortKey = key
        this.sortDirection = 'asc'
      }
      this.currentPage = 1
    },

    getSeverityColor (severity) {
      switch (severity) {
        case 2:
          return 'bg-orange'
        case 3:
          return 'bg-red'
        default:
          return ''
      }
    },

    async refreshData () {
      if (this.companyId) {
        this.currentPage = 1
        this.sortKey = ''
        this.sortDirection = 'default'
        this.selectedSearchItem = ''
        this.searchQuery = ''
        this.showSuggestions = false
        this.selectedIndex = -1
        this.$emit('updating')
        await this.fetchAllData(this.companyId)
      }
    },

    async downloadData () {
      if (this.downloading) return

      this.downloading = true
      try {
        const response = await DashboardService.getLastStatusExport(
          this.currentUser.UserId,
          this.companyId,
          { responseType: 'blob' }
        )

        const baseFilename = 'status_export'
        const timestamp = new Date().toISOString().replace(/[:.-]/g, '').slice(0, 14)
        const companyIdOrName = this.companyName || this.companyId || 'Unknown'
        const filename = `${baseFilename}_${companyIdOrName}_${timestamp}.xlsx`

        const url = window.URL.createObjectURL(new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.errorMessage = 'Failed to download the file'
      } finally {
        this.downloading = false
      }
    },

    clearSearchClick () {
      this.selectedSearchItem = ''
      this.searchQuery = ''
      this.showSuggestions = false
      this.selectedIndex = -1
      this.isSelecting = false
      this.updateTable()
    },

    selectSuggestion (suggestion) {
      this.isSelecting = true
      this.selectedSearchItem = suggestion
      this.searchQuery = suggestion
      this.showSuggestions = false
      this.selectedIndex = -1
      this.$nextTick(() => {
        this.isSelecting = false
      })
      this.updateTable()
    },

    moveSelection (delta) {
      if (!this.showSuggestions || this.filteredSuggestions.length === 0) return

      const newIndex = this.selectedIndex + delta
      if (newIndex >= 0 && newIndex < this.filteredSuggestions.length) {
        this.selectedIndex = newIndex
      } else if (newIndex < 0) {
        this.selectedIndex = this.filteredSuggestions.length - 1
      } else if (newIndex >= this.filteredSuggestions.length) {
        this.selectedIndex = 0
      }
    },

    selectHighlightedSuggestion () {
      this.isSelecting = true
      if (this.searchQuery.trim() === '') {
        // Clear filter if textbox is empty
        this.selectedSearchItem = ''
        this.showSuggestions = false
        this.selectedIndex = -1
        this.isSelecting = false
        this.updateTable()
      } else if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredSuggestions.length) {
        this.selectSuggestion(this.filteredSuggestions[this.selectedIndex])
      } else if (this.filteredSuggestions.length > 0) {
        this.selectSuggestion(this.filteredSuggestions[0])
      }
      this.$nextTick(() => {
        this.showSuggestions = false
      })
    },

    onInput () {
      if (!this.isSelecting && this.searchQuery) {
        this.showSuggestions = true
      }
    },

    handleClickOutside (event) {
      const autocompleteContainer = this.$el.querySelector('.autocomplete-container')
      if (autocompleteContainer && !autocompleteContainer.contains(event.target)) {
        this.showSuggestions = false
        this.selectedIndex = -1
      }
    },

    nextPage () {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },

    prevPage () {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },

    updateRowsPerPage () {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.table-container')
        const tableWrapper = this.$el.querySelector('.table-wrapper')
        const tableHeader = this.$el.querySelector('.status-table thead')

        if (container && tableWrapper && tableHeader) {
          const rowHeight = 32 // Row height
          const headerHeight = tableHeader.clientHeight || 40 // Header height
          const paginationHeight = 43 // Pagination controls

          // Calculate available height for table rows
          const availableHeight = tableWrapper.clientHeight - headerHeight - paginationHeight

          // Calculate max number of rows that fit
          this.rowsPerPage = Math.max(1, Math.floor(availableHeight / rowHeight))

          // Ensure pagination updates
          if (this.currentPage > this.totalPages) {
            this.currentPage = Math.max(1, this.totalPages)
          }
        }
      })
    },

    translateOperationMode (operationMode) {
      switch (operationMode) {
        case 1:
          return this.$t('operational')
        case 5:
          return this.$t('maintenance')
        default:
          return this.$t('unknown')
      }
    },

    updateTable () {
      this.currentPage = 1
    },

    goToDashboard3 () {
      this.$router.push(`/dashboard3`)
    },

    goToPath (railObjectId) {
      this.$router.push(`/switch/${railObjectId}`)
    },

    goToMeasurement (railObjectId, measurementId) {
      this.$router.push(`/switch/${railObjectId}?measurementId=${measurementId}`)
    }
  }
}
</script>

<style scoped>
.content-wrapper {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.table-container {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  min-height: 100px;
}

.table-wrapper {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

.pagination {
  position: sticky;
  bottom: 4px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 0;
  height: 24px;
  box-sizing: border-box;
  z-index: 1;
}

.pagination-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  line-height: 24px;
  transition: color 0.3s ease;
}

.pagination-btn:disabled {
  opacity: 0.5;
}

.pagination-btn .material-icons {
  font-size: 24px;
  color: #666;
}

.pagination-btn:hover:not(:disabled) .material-icons {
  color: #3498db;
}

.status-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0px;
}

.status-table th,
.status-table td {
  border: 1px solid #ccc;
  padding: 5px;
  text-align: center;
}

.status-table td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.status-table th {
  background-color: #f4f4f4;
  cursor: pointer;
  user-select: none;
}

.status-table th:hover {
  background-color: #e0e0e0;
}

.status-table td.clickable {
  cursor: pointer;
}

.status-table td.clickable:hover {
  color: #1e6bb8;
}

.bg-red {
  background-color: rgba(255, 0, 0, 0.432);
}

.bg-orange {
  background-color: rgba(255, 165, 0, 0.3);
}

.bg-blue {
  background-color: rgba(0, 0, 255, 0.3);
}

.bg-green {
  background-color: rgba(0, 255, 0, 0.3);
}

.spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 200px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.spinner {
  border: 6px solid #f3f3f3;
  border-top: 6px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-message {
  display: flex;
  align-items: center;
  background-color: rgba(255, 0, 0, 0.1);
  color: #d32f2f;
  border: 1px solid #d32f2f;
  padding: 10px;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 500;
  margin: 10px;
  width: fit-content;
  max-width: 100%;
  text-align: center;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.5s ease-in-out;
}

.error-icon {
  margin-right: 8px;
  font-size: 20px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.last-status-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  padding-left: 10px;
  padding-right: 10px;
}

.last-status-container h6 {
  margin: 0;
  line-height: 1.2;
}

.last-status-container p {
  margin: 0;
  line-height: 1.2;
  color: darkgray;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.controls {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: white;
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 12px;
  height: 34px;
  flex-wrap: nowrap;
}

.left-controls {
  display: flex;
  gap: 5px;
  align-items: center;
}

.right-controls {
  display: flex;
  gap: 5px;
  align-items: center;
  position: relative;
}

.controls .refresh-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  height: 24px;
  line-height: 24px;
}

.controls .refresh-button .material-icons {
  font-size: 20px;
  color: #666;
}

.controls .refresh-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.controls .refresh-button.loading .material-icons {
  animation: spin 1s linear infinite;
}

.autocomplete-container {
  position: relative;
}

.autocomplete-input {
  padding: 2px 4px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 12px;
  height: 24px;
  width: 175px;
  box-sizing: border-box;
}

.suggestions-list {
  position: absolute;
  top: 28px;
  left: 0;
  width: 175px;
  max-height: 150px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ccc;
  border-radius: 3px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  list-style: none;
  padding: 0;
  margin: 0;
  z-index: 1001;
}

.suggestion-item {
  padding: 2px 4px;
  cursor: pointer;
  font-size: 12px;
  color: #333;
}

.suggestion-item.highlighted {
  background-color: #f4f4f4;
}

.suggestion-item:hover {
  background-color: #e0e0e0;
}

.tooltip {
  position: relative;
  display: inline-block;
  background-color: black;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 5px 0;
  border-bottom: 1px dotted black;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/dashboard2/_LastStatus.vue